/**
 * Mobile Phase 2, chunk 2 (docs/mobile-design.md §4.11): the Tasks tab. My open tasks grouped Overdue / Today /
 * Upcoming / No date, and one-line quick capture: "Restack bay 12 tomorrow @James !high" becomes a title, a due date,
 * an assignee and a priority, shown as chips before it is saved. Pure TS (luxon for dates).
 */
import { DateTime } from 'luxon';
import type { TaskPriority } from '../types';
import type { TaskCreateFields } from '../task-create';

const TZ = 'Europe/London';

// ------------------------------------------------------------------------------------------------ grouping
export type TaskGroupId = 'overdue' | 'today' | 'upcoming' | 'none';
export const TASK_GROUP_LABEL: Record<TaskGroupId, string> = { overdue: 'Overdue', today: 'Today', upcoming: 'Upcoming', none: 'No date' };
export interface GroupableTask { id: string; due_date: string | null; due_time?: string | null; priority: TaskPriority; completed_at: string | null; title: string }
const RANK: Record<TaskPriority, number> = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 };

export function taskGroupOf(t: Pick<GroupableTask, 'due_date'>, today: string): TaskGroupId {
  if (!t.due_date) return 'none';
  const d = t.due_date.slice(0, 10);
  return d < today ? 'overdue' : d === today ? 'today' : 'upcoming';
}
/** Open tasks only, in group order; empty groups left out. Dated groups by date then time then priority; No date by priority. */
export function groupMyTasks<T extends GroupableTask>(tasks: T[], today: string): { id: TaskGroupId; label: string; tasks: T[] }[] {
  const by: Record<TaskGroupId, T[]> = { overdue: [], today: [], upcoming: [], none: [] };
  for (const t of tasks) if (!t.completed_at) by[taskGroupOf(t, today)].push(t);
  const key = (t: T) => `${t.due_date?.slice(0, 10) ?? '9999'}|${t.due_time ?? '99:99'}|${RANK[t.priority] ?? 9}|${t.title.toLowerCase()}`;
  return (Object.keys(by) as TaskGroupId[]).filter(g => by[g].length).map(g => ({
    id: g, label: TASK_GROUP_LABEL[g],
    tasks: by[g].sort((a, b) => (g === 'none' ? (RANK[a.priority] - RANK[b.priority]) || a.title.localeCompare(b.title) : key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0)),
  }));
}
/** "Overdue · Mon 19 Oct", "Today", "Today 15:00", "Tomorrow", "Fri 23 Oct". */
export function taskDueLabel(due: string | null, today: string, time?: string | null): string | null {
  if (!due) return null;
  const d = DateTime.fromISO(due.slice(0, 10), { zone: TZ });
  const t = DateTime.fromISO(today, { zone: TZ });
  const days = Math.round(d.diff(t, 'days').days);
  const at = time ? ` ${time.slice(0, 5)}` : '';
  if (days < 0) return `Overdue · ${d.toFormat('ccc d LLL')}`;
  if (days === 0) return `Today${at}`;
  if (days === 1) return `Tomorrow${at}`;
  return `${d.toFormat('ccc d LLL')}${at}`;
}
/** How long the Undo stays after completing a task from the list. */
export const TASK_UNDO_MS = 5000;

// ------------------------------------------------------------------------------------------------ quick capture
export interface QuickPerson { user_id: string; display_name: string }
export type QuickChip =
  | { kind: 'due'; label: string; token: string }
  | { kind: 'time'; label: string; token: string }
  | { kind: 'priority'; label: string; token: string }
  | { kind: 'assignee'; label: string; token: string; user_id: string }
  | { kind: 'unknown_person'; label: string; token: string };
export interface QuickTask {
  title: string;
  due_date: string | null;
  due_time: string | null;
  priority: TaskPriority | null;
  assignee_ids: string[];
  chips: QuickChip[];
}
const PRIORITY: Record<string, TaskPriority> = { urgent: 'urgent', u: 'urgent', '!': 'urgent', high: 'high', h: 'high', medium: 'medium', med: 'medium', m: 'medium', low: 'low', l: 'low' };
export const PRIORITY_LABEL: Record<TaskPriority, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low', none: 'No priority' };
/** t218u: one colour per priority (desktop and phone): Urgent red, High orange, Medium blue, Low slate. Never yellow. */
export const PRIORITY_COLOUR: Record<TaskPriority, string> = { urgent: '#dc2626', high: '#ea580c', medium: '#2563eb', low: '#64748b', none: '#cbd5e1' };
const WEEKDAYS: Record<string, number> = { mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2, wed: 3, weds: 3, wednesday: 3, thu: 4, thur: 4, thurs: 4, thursday: 4, fri: 5, friday: 5, sat: 6, saturday: 6, sun: 7, sunday: 7 };
const MONTHS: Record<string, number> = { jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12 };

const dayLabel = (d: DateTime, today: DateTime) => {
  const n = Math.round(d.startOf('day').diff(today.startOf('day'), 'days').days);
  return n === 0 ? `Today · ${d.toFormat('ccc d LLL')}` : n === 1 ? `Tomorrow · ${d.toFormat('ccc d LLL')}` : d.toFormat('cccc d LLL');
};
/** The future date for day + month (this year, or next year once it has passed). */
const nextDayMonth = (day: number, month: number, today: DateTime): DateTime | null => {
  let d = DateTime.fromObject({ year: today.year, month, day }, { zone: TZ });
  if (!d.isValid) return null;
  if (d < today.startOf('day')) d = d.plus({ years: 1 });
  return d;
};

/**
 * Match a date phrase at the start of `words` (lower-case). Returns the date and how many words it used.
 * today, tomorrow / tmrw, a weekday (the next one; the same weekday means next week), next week (Monday),
 * in N days / weeks, 23 Oct / Oct 23, 23/10. "on", "by" and "due" in front are eaten too.
 */
function matchDate(words: string[], today: DateTime): { date: DateTime; used: number } | null {
  let lead = 0;
  if (['on', 'by', 'due'].includes(words[0] ?? '') && words.length > 1) lead = 1;
  const w = words.slice(lead);
  const a = w[0] ?? '', b = w[1] ?? '', c = w[2] ?? '';
  const hit = (date: DateTime | null, used: number) => (date ? { date, used: used + lead } : null);
  if (a === 'today' || a === 'tonight') return hit(today, 1);
  if (a === 'tomorrow' || a === 'tmrw' || a === 'tmr') return hit(today.plus({ days: 1 }), 1);
  if (a === 'next' && b === 'week') return hit(today.plus({ weeks: 1 }).startOf('week'), 2);
  if (a === 'next' && WEEKDAYS[b]) return hit(today.plus({ days: ((WEEKDAYS[b]! - today.weekday + 7) % 7) || 7 }), 2);
  if (WEEKDAYS[a]) return hit(today.plus({ days: ((WEEKDAYS[a]! - today.weekday + 7) % 7) || 7 }), 1);
  if (a === 'in' && /^\d{1,3}$/.test(b) && /^(days?|weeks?)$/.test(c)) return hit(today.plus(c.startsWith('w') ? { weeks: Number(b) } : { days: Number(b) }), 3);
  const num = /^(\d{1,2})(?:st|nd|rd|th)?$/;
  if (num.test(a) && MONTHS[b]) return hit(nextDayMonth(Number(num.exec(a)![1]), MONTHS[b]!, today), 2);
  if (MONTHS[a] && num.test(b)) return hit(nextDayMonth(Number(num.exec(b)![1]), MONTHS[a]!, today), 2);
  const slash = /^(\d{1,2})\/(\d{1,2})$/.exec(a);
  if (slash) return hit(nextDayMonth(Number(slash[1]), Number(slash[2]), today), 1);
  return null;
}
/** 15:00, 3pm, 3:30pm, "at 9am". */
function matchTime(words: string[]): { time: string; used: number } | null {
  const lead = words[0] === 'at' && words.length > 1 ? 1 : 0;
  const m = /^(\d{1,2})(?::(\d{2}))?(am|pm)?$/.exec(words[lead] ?? '');
  if (!m || (!m[2] && !m[3])) return null;
  let h = Number(m[1]); const min = Number(m[2] ?? 0);
  if (m[3] === 'pm' && h < 12) h += 12;
  if (m[3] === 'am' && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return { time: `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`, used: lead + 1 };
}
/** @James, @james.wilson, @"James Wilson"-style two words when they make a full name. */
function matchPerson(tokens: string[], i: number, people: QuickPerson[]): { person: QuickPerson | null; used: number; token: string } {
  const first = tokens[i]!.slice(1).replace(/[.,;:!?]+$/, '');
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const two = tokens[i + 1] ? `${first} ${tokens[i + 1]!.replace(/[.,;:!?]+$/, '')}` : null;
  if (two) {
    const full = people.filter(p => norm(p.display_name) === norm(two));
    if (full.length === 1) return { person: full[0]!, used: 2, token: `@${two}` };
  }
  const exact = people.filter(p => norm(p.display_name) === norm(first));
  if (exact.length === 1) return { person: exact[0]!, used: 1, token: tokens[i]! };
  const byFirst = people.filter(p => norm(p.display_name.split(/\s+/)[0] ?? '') === norm(first));
  if (byFirst.length === 1) return { person: byFirst[0]!, used: 1, token: tokens[i]! };
  const prefix = people.filter(p => norm(p.display_name).startsWith(norm(first)));
  return { person: prefix.length === 1 ? prefix[0]! : null, used: 1, token: tokens[i]! };
}

/**
 * Parse one line of quick capture. Recognised parts are removed from the title; anything not understood stays in it,
 * and an @name that matches nobody (or several people) is kept in the title and flagged with an unknown_person chip.
 */
export function parseQuickTask(text: string, o: { people?: QuickPerson[]; today?: string } = {}): QuickTask {
  const today = DateTime.fromISO(o.today ?? DateTime.now().setZone(TZ).toISODate()!, { zone: TZ });
  const tokens = text.trim().split(/\s+/).filter(Boolean);
  const keep: string[] = [];
  const out: QuickTask = { title: '', due_date: null, due_time: null, priority: null, assignee_ids: [], chips: [] };
  for (let i = 0; i < tokens.length;) {
    const tok = tokens[i]!;
    const lower = tokens.slice(i, i + 4).map(t => t.toLowerCase().replace(/[.,;]+$/, ''));
    // !high, !urgent, !!, !1-!3
    const bang = /^!(!|[a-z]+|[1-3])$/i.exec(tok.replace(/[.,;]+$/, ''));
    if (bang && !out.priority) {
      const v = bang[1]!.toLowerCase();
      const p = PRIORITY[v] ?? ({ 1: 'urgent', 2: 'high', 3: 'medium' } as Record<string, TaskPriority>)[v];
      if (p) { out.priority = p; out.chips.push({ kind: 'priority', label: PRIORITY_LABEL[p], token: tok }); i += 1; continue; }
    }
    if (tok.startsWith('@') && tok.length > 1) {
      const m = matchPerson(tokens, i, o.people ?? []);
      if (m.person) {
        if (!out.assignee_ids.includes(m.person.user_id)) { out.assignee_ids.push(m.person.user_id); out.chips.push({ kind: 'assignee', label: m.person.display_name, token: m.token, user_id: m.person.user_id }); }
        i += m.used; continue;
      }
      out.chips.push({ kind: 'unknown_person', label: `No one called ${tok}`, token: tok });
      keep.push(tok); i += 1; continue;
    }
    if (!out.due_date) {
      const d = matchDate(lower, today);
      if (d) {
        out.due_date = d.date.toISODate();
        out.chips.push({ kind: 'due', label: dayLabel(d.date, today), token: tokens.slice(i, i + d.used).join(' ') });
        i += d.used; continue;
      }
    }
    if (!out.due_time) {
      const t = matchTime(lower);
      if (t) { out.due_time = t.time; out.chips.push({ kind: 'time', label: t.time, token: tokens.slice(i, i + t.used).join(' ') }); i += t.used; continue; }
    }
    keep.push(tok); i += 1;
  }
  // A time with no date means today (or tomorrow once it has passed is the caller's call; we keep today).
  if (out.due_time && !out.due_date) {
    out.due_date = today.toISODate();
    out.chips.unshift({ kind: 'due', label: dayLabel(today, today), token: '' });
  }
  out.title = keep.join(' ').replace(/\s+([,.;:])/g, '$1').replace(/^[,.;:\s-]+|[,;:\s-]+$/g, '');
  return out;
}
/** The create body; nobody named means it's for me. */
export function quickTaskBody(q: QuickTask, me: string): TaskCreateFields {
  return {
    title: q.title,
    ...(q.priority ? { priority: q.priority } : {}),
    ...(q.due_date ? { due_date: q.due_date } : {}),
    ...(q.due_time ? { due_time: q.due_time } : {}),
    assignee_ids: q.assignee_ids.length ? q.assignee_ids : [me],
  };
}
/** Why the capture can't be saved yet, or null. */
export const quickTaskBlocker = (q: QuickTask): string | null => (!q.title.trim() ? 'Type what needs doing' : q.title.length > 300 ? 'Keep the title under 300 characters' : null);

// ------------------------------------------------------------------------------------------------ t194u/t196u: search and filters
export type MyTaskFilter = 'all' | 'overdue' | 'today' | 'upcoming' | 'none' | 'done';
/** "All" is every open task; Done is its own list (newest first). */
export const MY_TASK_FILTERS: { id: MyTaskFilter; label: string }[] = [
  { id: 'all', label: 'All' }, { id: 'overdue', label: 'Overdue' }, { id: 'today', label: 'Today' }, { id: 'upcoming', label: 'Upcoming' }, { id: 'none', label: 'No date' }, { id: 'done', label: 'Done' },
];
export interface SearchableTask extends GroupableTask { key?: string | null; project_name?: string | null; location_name?: string | null; assignee_ids?: string[]; description?: string | null; tags?: string[] | null; no_project?: boolean }
/** Every word of the search is somewhere in the title, the (hidden) reference, description, tags, project, building or an assignee's name. */
export function taskMatches(t: SearchableTask, query: string, nameOf: (userId: string) => string | null | undefined): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [t.title, t.key, t.description, ...(t.tags ?? []), t.no_project ? null : t.project_name, t.location_name, ...(t.assignee_ids ?? []).map(nameOf)]
    .filter(Boolean).join(' ').toLowerCase();
  return words.every(w => hay.includes(w));
}
/** Counts per chip (open tasks; Done counts the done list). */
export function myTaskCounts(open: GroupableTask[], done: GroupableTask[] | undefined, today: string): Record<MyTaskFilter, number | undefined> {
  const c: Record<MyTaskFilter, number | undefined> = { all: 0, overdue: 0, today: 0, upcoming: 0, none: 0, done: done?.length };
  for (const t of open) { if (t.completed_at) continue; c.all!++; c[taskGroupOf(t, today)]!++; }
  return c;
}
/** Groups for a chip: All keeps the four groups; a date chip only its group; Done one list, newest completed first. */
export function filterMyTasks<T extends GroupableTask>(open: T[], done: T[], filter: MyTaskFilter, today: string): { id: TaskGroupId | 'done'; label: string; tasks: T[] }[] {
  if (filter === 'done') {
    const list = done.filter(t => t.completed_at).sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''));
    return list.length ? [{ id: 'done', label: 'Done', tasks: list }] : [];
  }
  const groups = groupMyTasks(open, today);
  return filter === 'all' ? groups : groups.filter(g => g.id === filter);
}
/** How long the circle must be held to complete (or reopen) a task. */
export const HOLD_TO_COMPLETE_MS = 550;

// ------------------------------------------------------------------------------------------------ t198u: private <-> shared
export interface PrivacyTask { is_private?: boolean; no_project?: boolean; parent_task_id?: string | null; created_by: string; assignee_ids: string[]; team_ids?: string[] | null; location_id?: string | null }
/**
 * Who may flip a task between private (only you) and shared: only its creator, only a No project top-level task, and
 * (to make it private) nobody else on it - no other assignee, team or building. Desktop's task panel and the phone use this.
 */
export function taskPrivacy(t: PrivacyTask, me: string | null | undefined): { action: 'make_private' | 'make_shared' | null; reason: string | null } {
  if (!me || t.created_by !== me) return { action: null, reason: null };
  if (t.parent_task_id) return { action: null, reason: null };
  if (t.is_private) return { action: 'make_shared', reason: null };
  if (!t.no_project) return { action: null, reason: null };
  const others = t.assignee_ids.some(id => id !== me) || (t.team_ids?.length ?? 0) > 0 || Boolean(t.location_id);
  return others ? { action: null, reason: 'Remove other people, teams and the building first' } : { action: 'make_private', reason: null };
}

// ------------------------------------------------------------------------------------------------ t214u: My checklists segment
export type MyChecklistFilter = 'outstanding' | 'today' | 'overdue' | 'completed' | 'all';
/** t224u: the phone's All checklists screen (every run you can see): Outstanding by default. */
export const ALL_CHECKLIST_FILTERS: { id: MyChecklistFilter; label: string }[] = [
  { id: 'outstanding', label: 'Outstanding' }, { id: 'overdue', label: 'Overdue' }, { id: 'completed', label: 'Completed' }, { id: 'all', label: 'All' },
];
/** t224u: who a run is for, short: "Tom Evans", "Whoever is on shift" or the role. */
export function checklistAssignee(r: { assignee_type: string; assignee_name: string | null }): string | null {
  if (r.assignee_name) return r.assignee_name;
  return r.assignee_type === 'on_shift' ? 'Whoever is on shift' : null;
}
/**
 * t703u: who a checklist run was for and who did it, for list rows. When the assignee (or starter) is the person who
 * completed it, they're named once ("Done by Sam"); otherwise both ("Started by Sam · Done by Alex"). `who` is null when
 * there's nothing to say before "Done by"; `doneBy` is null until it's completed.
 */
export function checklistRunPeople(r: { status: string; assignee_type: string; assignee_id: string | null; assignee_name: string | null;
  started_by: string | null; completed_by: string | null; completed_by_name: string | null }): { who: string | null; doneBy: string | null } {
  const who = checklistAssignee(r);
  const doneBy = r.status === 'completed' && r.completed_by_name ? r.completed_by_name : null;
  if (!doneBy || !who) return { who, doneBy };
  const user = r.assignee_type === 'user';
  if (user && (r.assignee_id ? r.assignee_id === r.completed_by : r.assignee_name === r.completed_by_name)) return { who: null, doneBy };
  return { who: user && r.started_by && r.started_by === r.assignee_id ? `Started by ${who}` : who, doneBy };
}
export const MY_CHECKLIST_FILTERS: { id: MyChecklistFilter; label: string }[] = [
  { id: 'outstanding', label: 'Outstanding' }, { id: 'today', label: 'Today' }, { id: 'overdue', label: 'Overdue' }, { id: 'completed', label: 'Completed' },
];
export interface FilterableChecklist { template_name: string; location_name: string; local_date: string; status: string; late: boolean; due_by: string | null; completed_at: string | null }
/** Outstanding = not completed (open, in progress or missed): the same rule as the Tasks tab's "Checklists due" row. */
export const checklistOutstanding = (r: Pick<FilterableChecklist, 'status'>) => r.status !== 'completed' && r.status !== 'missed';
const checklistOverdue = (r: FilterableChecklist) => r.status !== 'completed' && (r.late || r.status === 'missed');
export function checklistMatches(r: FilterableChecklist, filter: MyChecklistFilter, today: string, query = ''): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hay = `${r.template_name} ${r.location_name}`.toLowerCase();
  if (!words.every(w => hay.includes(w))) return false;
  switch (filter) {
    case 'outstanding': return checklistOutstanding(r) || checklistOverdue(r);
    case 'today': return r.local_date === today && r.status !== 'completed';
    case 'overdue': return checklistOverdue(r);
    case 'completed': return r.status === 'completed';
    case 'all': return true;
  }
}
// ------------------------------------------------------------------------------------------------ t329u: My checklists by date
/** The date choices, in order (the date range control's presets; 'any' reads "All"). */
export const CHECKLIST_DUE_PRESETS = ['any', 'today', 'tomorrow', 'this-week', 'overdue', 'custom'] as const;
export type ChecklistDuePreset = (typeof CHECKLIST_DUE_PRESETS)[number];
export const CHECKLIST_DUE_LABELS: Record<ChecklistDuePreset, string> = { any: 'All', today: 'Today', tomorrow: 'Tomorrow', 'this-week': 'This week', overdue: 'Overdue', custom: 'Custom range' };
/** How far back "Overdue" looks (the API caps a range at 92 days). */
export const CHECKLIST_OVERDUE_DAYS = 30;
const addDays = (iso: string, n: number) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
/** The days to ask the API for (null = the usual yesterday-and-today list). Custom uses the picked days; weeks start on Monday. */
export function checklistDueRange(v: { preset: string; from: string; to: string }, today: string): { from: string; to: string } | null {
  switch (v.preset) {
    case 'today': return { from: today, to: today };
    case 'tomorrow': { const t = addDays(today, 1); return { from: t, to: t }; }
    case 'this-week': { const dow = (new Date(`${today}T12:00:00Z`).getUTCDay() + 6) % 7; const mon = addDays(today, -dow); return { from: mon, to: addDays(mon, 6) }; }
    case 'overdue': return { from: addDays(today, -CHECKLIST_OVERDUE_DAYS), to: today };
    case 'custom': return v.from && v.to ? { from: v.from <= v.to ? v.from : v.to, to: v.from <= v.to ? v.to : v.from } : null;
    default: return null;
  }
}
/** Overdue by date: not done and missed, late, or past its due time. */
export const checklistPastDue = (r: Pick<FilterableChecklist, 'status' | 'late' | 'due_by'>, nowIso: string) =>
  r.status !== 'completed' && r.status !== 'scheduled' && (r.status === 'missed' || r.late || Boolean(r.due_by && r.due_by < nowIso));
/** The API returns the range; only "Overdue" narrows further. */
export const checklistDueMatches = (r: Pick<FilterableChecklist, 'status' | 'late' | 'due_by'>, preset: string, nowIso: string) =>
  preset !== 'overdue' || checklistPastDue(r, nowIso);
/** The calm empty line for a date choice ('' for All, which keeps the screen's own). */
export function checklistDueEmpty(preset: string): string {
  switch (preset) {
    case 'today': return 'Nothing due today.';
    case 'tomorrow': return 'Nothing due tomorrow.';
    case 'this-week': return 'Nothing due this week.';
    case 'overdue': return 'Nothing overdue.';
    case 'custom': return 'Nothing due on these dates.';
    default: return '';
  }
}
/** A run that hasn't been created yet (a later day): read-only, no runner. */
export const checklistScheduled = (r: { status: string }) => r.status === 'scheduled';

/** Due first (overdue at the top), then by due time; completed newest first. */
export function sortChecklists<T extends FilterableChecklist>(list: T[], filter: MyChecklistFilter): T[] {
  if (filter === 'completed') return [...list].sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''));
  return [...list].sort((a, b) => Number(checklistOverdue(b)) - Number(checklistOverdue(a)) || (a.due_by ?? '~').localeCompare(b.due_by ?? '~'));
}
export const checklistCounts = (list: FilterableChecklist[], today: string): Record<MyChecklistFilter, number> =>
  ({ outstanding: list.filter(r => checklistMatches(r, 'outstanding', today)).length, today: list.filter(r => checklistMatches(r, 'today', today)).length,
    overdue: list.filter(r => checklistMatches(r, 'overdue', today)).length, completed: list.filter(r => r.status === 'completed').length, all: list.length });
